// nameMatch.js — Comparación de nombres de cliente, robusta y única para todo
// el sistema (pólizas, siniestros, etc.).
//
// El problema que resuelve: matcheos previos consideraban que dos nombres eran
// la misma persona si compartían UNA sola palabra. Eso provoca asociaciones
// catastróficas: "MALATES VERA JUAN EDGARDO" matcheaba con "PAZ, JUAN CARLOS"
// solo por compartir "JUAN". En un módulo crítico eso no puede pasar.
//
// Regla: dos nombres son la MISMA persona solo si comparten una proporción ALTA
// de palabras significativas (no nombres de pila comunes sueltos).

// Palabras que no identifican por sí solas (estructuras societarias, conectores
// y nombres de pila muy frecuentes que generan falsos positivos).
const STOPWORDS = new Set([
  'el', 'la', 'los', 'las', 'de', 'del', 'y', 'e', 'da', 'do',
  'sa', 'srl', 'sas', 'ltda', 'ltd', 's', 'r', 'l', 'cia', 'seguros',
]);

// Nombres de pila muy comunes en Uruguay: si lo ÚNICO en común es uno de estos,
// no alcanza para afirmar que es la misma persona.
const NOMBRES_COMUNES = new Set([
  'juan', 'jose', 'luis', 'carlos', 'maria', 'ana', 'jorge', 'pedro',
  'pablo', 'martin', 'diego', 'gabriel', 'fernando', 'sergio', 'daniel',
  'gonzalo', 'rodrigo', 'andres', 'marcelo', 'eduardo', 'roberto',
]);

export function normalizarNombre(s) {
  return (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')   // quita tildes
    .replace(/[.,;:()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Palabras significativas de un nombre (sin stopwords ni palabras cortas).
export function palabrasNombre(s) {
  return normalizarNombre(s)
    .split(' ')
    .filter(w => w.length >= 3 && !STOPWORDS.has(w));
}

/**
 * ¿Los nombres `a` y `b` corresponden a la MISMA persona/entidad?
 * Exige solapamiento alto, no una sola coincidencia.
 *
 * Criterio:
 *  - Se cuentan las palabras significativas en común.
 *  - Deben coincidir al menos el 60% de las palabras del nombre más corto.
 *  - Y al menos 2 palabras en común, EXCEPTO cuando el nombre más corto tiene
 *    una sola palabra (ej. razón social "BARRACA XX") y esa coincide.
 *  - Si la única coincidencia es un nombre de pila común (Juan, José…), NO basta.
 */
export function mismaPersona(a, b) {
  const pa = palabrasNombre(a);
  const pb = palabrasNombre(b);
  if (pa.length === 0 || pb.length === 0) return false;

  const setB = new Set(pb);
  const comunes = pa.filter(w => setB.has(w));
  if (comunes.length === 0) return false;

  // Si lo único en común es un nombre de pila común, no alcanza.
  const comunesUtiles = comunes.filter(w => !NOMBRES_COMUNES.has(w));
  if (comunesUtiles.length === 0 && comunes.length < 2) return false;

  const minLen = Math.min(pa.length, pb.length);
  const proporcion = comunes.length / minLen;

  if (minLen === 1) {
    // Nombre de una sola palabra (razón social corta): esa palabra debe coincidir
    // y no ser un nombre de pila común.
    return comunes.length === 1 && comunesUtiles.length === 1;
  }

  // Para nombres de 2+ palabras: al menos 2 en común y 60% de solapamiento.
  return comunes.length >= 2 && proporcion >= 0.6;
}
